const BRACKET_REFERENCE_PATTERN = /\[(@?[A-Z0-9_#]+)\]/gi;
const PLACEHOLDER_PATTERN = /\[([A-Z0-9_#]+)\]/gi;
const IF_PATTERN = /^\s*\[([A-Z0-9_#]+)\]\s*(<>|!=|=|==|<=|>=|<|>)\s*(".*?"|'.*?'|\[@?[A-Z0-9_#]+\]|[^\s]+)(?:\s+THEN\s+(.+))?/i;
const COMMAND_PATTERN = /^(CALL|GOTO|WRITE|READ|COMPUTE|SSR|EMUFIND)\b\s*(.*)$/i;
const REQUIRED_LOGIC_VALUE_PATTERN = /\[([A-Z0-9_]+(?:#D)?)\]\s*(=|==)\s*(?:"-"|'-'|""|''|-)/gi;
const SABRE_SPECIAL_SEQUENCE_PATTERN = /\u00A4\u00A5\u00B6\u00A4/g;

function createEmptyIr(title) {
  return {
    title,
    variables: {},
    catalogs: {},
    defaults: [],
    labels: [],
    buttonDefinitions: {},
    forms: [],
    menus: [],
    menuDebug: [],
    logic: [],
    calls: [],
    remarks: [],
    unresolved: [],
    diagnostics: [],
    lineClassifications: [],
    specialCharReport: {
      totalLinesWithSpecialChars: 0,
      chars: [],
    },
  };
}

function cleanVisibleLabel(label) {
  return label.replace(SABRE_SPECIAL_SEQUENCE_PATTERN, '').trim().replace(/\s*:\s*$/, '');
}

function findByName(collection, name) {
  if (!name) {
    return null;
  }

  if (collection[name]) {
    return collection[name];
  }

  const normalizedName = name.toUpperCase();
  const normalizedBaseName = stripCatalogSuffix(normalizedName);
  const key = Object.keys(collection).find((candidate) => {
    return candidate.toUpperCase() === normalizedName || stripCatalogSuffix(candidate).toUpperCase() === normalizedBaseName;
  });

  return key ? collection[key] : null;
}

function regexEscape(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function normalizeValue(rawValue) {
  const value = rawValue.trim();

  if (/^\[[A-Z0-9_]+\]$/i.test(value)) {
    return {
      type: 'variable',
      name: value.slice(1, -1),
    };
  }

  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return {
      type: 'literal',
      value: value.slice(1, -1),
    };
  }

  return {
    type: 'literal',
    value,
  };
}

function mergeVariableMetadata(field, variableCatalog) {
  const variable = findByName(variableCatalog, field.variable);

  if (!variable) {
    return field;
  }

  return {
    ...field,
    datatype: variable.datatype,
    required: variable.required,
    validationSource: variable.validationSource,
    maxlength: variable.length,
  };
}

function mergeCatalogMetadata(field, catalogs) {
  const expectsCatalog = /#D$/i.test(field.variable || '');
  const catalog = findByName(catalogs, field.variable);

  if (!catalog) {
    if (expectsCatalog) {
      return {
        ...field,
        type: 'select',
        control: 'select',
        variableBase: stripCatalogSuffix(field.variable),
        catalog: null,
        catalogMissing: true,
        options: [],
      };
    }

    return field;
  }

  return {
    ...field,
    type: 'select',
    control: 'select',
    variableBase: catalog.variable,
    catalog: catalog.id,
    catalogMissing: false,
    options: [...catalog.options],
  };
}

function enrichField(field, variableCatalog, catalogs) {
  if (!field.variable || isButtonReference(field.variable) || isSystemReference(field.variable)) {
    return field;
  }

  return mergeCatalogMetadata(mergeVariableMetadata(field, variableCatalog), catalogs);
}

function isButtonReference(reference) {
  return /^@(?:OK|EXIT|BTN_(?!PRSD).*)$/i.test(reference);
}

function isSystemReference(reference) {
  return /^@/i.test(reference) && !isButtonReference(reference);
}

function isNonVisualSystemReference(reference) {
  return /^@BTN_PRSD$/i.test(reference);
}

function getReferenceLabel(segment, reference) {
  const quotedMatches = [...segment.matchAll(/"([^"]*)"/g)];

  if (quotedMatches.length > 0) {
    return quotedMatches[quotedMatches.length - 1][1];
  }

  const plainLabel = segment
    .replace(/["'+]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return plainLabel || reference;
}

function createVisualControl(label, reference, variableCatalog, catalogs, line, raw, buttonDefinitions = {}) {
  const visualRequiredPattern = new RegExp(`\\[${regexEscape(reference)}\\]\\s*(?:"\\*"|\\*)`, 'i');
  const buttonDefinition = buttonDefinitions[reference] || null;
  const baseControl = {
    type: 'field',
    control: 'input',
    controlType: 'input',
    label: cleanVisibleLabel(label),
    variable: reference,
    name: stripCatalogSuffix(reference),
    binding: stripCatalogSuffix(reference),
    value: '',
    templateValue: `{${stripCatalogSuffix(reference)}}`,
    placeholder: stripCatalogSuffix(reference),
    editable: true,
    readonly: false,
    disabled: false,
    capturable: true,
    visualRequired: visualRequiredPattern.test(raw),
    visible: true,
    line,
    raw,
  };

  if (isButtonReference(reference)) {
    const button = {
      type: 'button',
      label: buttonDefinition?.label || cleanVisibleLabel(label) || reference,
      action: reference,
      button: reference,
      visible: true,
      buttonDefinition: buttonDefinition
        ? {
            label: buttonDefinition.label,
            line: buttonDefinition.line,
          }
        : undefined,
      line,
      raw,
    };

    if (/^@BTN_L/i.test(reference)) {
      button.diagnostic = {
        type: 'diagnostic',
        level: 'warning',
        code: 'UNKNOWN_SST_BUTTON',
        message: `Boton SST ${reference} detectado. Se conserva como boton especial, pero requiere validacion funcional.`,
        line,
        raw,
      };
    }

    return button;
  }

  if (isNonVisualSystemReference(reference)) {
    return {
      type: 'logicReference',
      visible: false,
      variable: reference,
      line,
      raw,
    };
  }

  if (isSystemReference(reference)) {
    return {
      type: 'system',
      label: cleanVisibleLabel(label) || reference,
      variable: reference,
      visible: true,
      line,
      raw,
    };
  }

  return enrichField(baseControl, variableCatalog, catalogs);
}

function hasCheckboxSuffix(reference) {
  return /#C$/i.test(reference || '');
}

function createCheckboxGroup(controls, label, line, raw) {
  return {
    type: 'checkbox-group',
    control: 'checkbox-group',
    controlType: 'checkbox-group',
    label: cleanVisibleLabel(label || ''),
    visible: true,
    options: controls.map((control) => ({
      label: control.label,
      variable: control.variable,
      suffix: '#C',
      line: control.line,
      raw: control.raw,
    })),
    line,
    raw,
  };
}

function collectBracketMatches(raw) {
  const matches = [];
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(raw)) !== null) {
    matches.push({
      reference: match[1],
      start: match.index,
      end: BRACKET_REFERENCE_PATTERN.lastIndex,
    });
  }

  return matches;
}

function isVisualRequiredMarker(segment = '') {
  return /^\s*(?:"\*"|'\*'|\*)\s*$/.test(segment);
}

function cleanCompositeText(segment = '') {
  if (isVisualRequiredMarker(segment)) {
    return '';
  }

  return segment
    .replace(SABRE_SPECIAL_SEQUENCE_PATTERN, '')
    .replace(/"([^"]*)"/g, '$1')
    .replace(/'([^']*)'/g, '$1')
    .replace(/(?:"\*"|'\*'|\*)\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function getCompositeHelperText(segment = '') {
  const cleanedText = cleanCompositeText(segment);
  const helperMatch = cleanedText.match(/^\(([^)]*)\)$/);

  return helperMatch ? cleanVisibleLabel(helperMatch[1]) : null;
}

function hasCompositeVisualRequired(raw) {
  return /\]\s*(?:"\*"|'\*'|\*)/i.test(raw);
}

function isCompositeFieldReference(reference) {
  return !isButtonReference(reference) && !isSystemReference(reference);
}

function createCompositeFieldPart(reference, variableCatalog, catalogs, line, raw, buttonDefinitions = {}) {
  const control = createVisualControl(stripCatalogSuffix(reference), reference, variableCatalog, catalogs, line, raw, buttonDefinitions);

  if (!['field', 'select'].includes(control.type)) {
    return null;
  }

  return {
    ...control,
    label: stripCatalogSuffix(reference),
  };
}

function createCompositeFieldRow(raw, matches, variableCatalog, catalogs, line, buttonDefinitions = {}) {
  const firstMatch = matches[0];
  const label = cleanVisibleLabel(cleanCompositeText(raw.slice(0, firstMatch.start)));
  const parts = [];

  matches.forEach((match, index) => {
    const previousMatch = matches[index - 1];
    const previousEnd = previousMatch ? previousMatch.end : firstMatch.start;
    const segmentBefore = raw.slice(previousEnd, match.start);

    if (index > 0) {
      const literal = cleanCompositeText(segmentBefore);

      if (literal) {
        parts.push({
          type: 'literal',
          value: literal,
          visible: true,
          line,
          raw: segmentBefore,
        });
      }
    }

    const fieldPart = createCompositeFieldPart(match.reference, variableCatalog, catalogs, line, raw, buttonDefinitions);

    if (!fieldPart) {
      return;
    }

    const trailingSegment = raw.slice(match.end, matches[index + 1]?.start ?? raw.length);
    const helperText = getCompositeHelperText(trailingSegment);

    parts.push(helperText ? { ...fieldPart, helperText } : fieldPart);
  });

  const trailingSegment = raw.slice(matches[matches.length - 1].end);
  const trailingLiteral = cleanCompositeText(trailingSegment);

  if (trailingLiteral && !getCompositeHelperText(trailingSegment)) {
    parts.push({
      type: 'literal',
      value: trailingLiteral,
      visible: true,
      line,
      raw: trailingSegment,
    });
  }

  return {
    type: 'field_row',
    control: 'field_row',
    controlType: 'field_row',
    label,
    visualRequired: hasCompositeVisualRequired(raw),
    parts,
    visible: true,
    line,
    raw,
  };
}

function extractTextControl(raw, line) {
  const text = raw.trim().replace(SABRE_SPECIAL_SEQUENCE_PATTERN, '').replace(/"([^"]*)"/g, '$1');
  const label = cleanVisibleLabel(text);

  if (!label) {
    return null;
  }

  return {
    type: 'text',
    label,
    text: label,
    visible: true,
    line,
    raw,
  };
}

function extractMenuText(raw) {
  const textMatch = raw.match(/"([^"]*)"/);
  const text = textMatch ? textMatch[1] : raw;

  return cleanVisibleLabel(text);
}

function safeMenuValue(text = '') {
  return String(text || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function isMenuSeparator(text) {
  return /^-+$/.test(text);
}

function normalizeCommandVariable(variableName) {
  return variableName.replace(/#[A-Z]$/i, '');
}

function extractSabreCommandBody(raw) {
  const normalizedRaw = raw.trim().replace(/\u00c2(?=[\u00a4\u00af\u00ae\ufffd])/g, '');

  if (!/^[\u00a4\u00af\ufffd]/.test(normalizedRaw)) {
    return null;
  }

  return normalizedRaw.slice(1).replace(/[\u00ae\u00a4\ufffd]+$/g, '');
}

function parseSabreCommandAction(raw) {
  const commandBody = extractSabreCommandBody(raw);

  if (!commandBody) {
    return null;
  }

  return {
    type: 'command',
    template: commandBody
      .replace(/\{ENTER\}/gi, '')
      .replace(/[\u009d\u00a4\ufffd]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/\[(@?[A-Z0-9_#]+)\]/gi, (_match, variableName) => `{${normalizeCommandVariable(variableName)}}`)
      .trim(),
  };
}

function createActionFromNode(node) {
  if (node.type === 'command') {
    const commandRaw = node.normalizedRaw || node.raw;

    return {
      type: 'command',
      template: node.lineClassification?.template || parseSabreCommandAction(commandRaw)?.template || node.expression || node.raw,
      visible: false,
      line: node.line,
      raw: node.raw,
      normalizedRaw: node.normalizedRaw,
    };
  }

  if (node.type === 'remark') {
    return {
      type: 'remark',
      remarkType: node.remarkType || 'Historical',
      template: node.template || node.text,
      skip: node.skip !== false,
      visible: false,
      line: node.line,
      raw: node.raw,
      normalizedRaw: node.normalizedRaw,
    };
  }

  if (node.type === 'ssr') {
    return {
      type: 'ssr',
      template: node.expression || node.raw,
      visible: false,
      line: node.line,
      raw: node.raw,
      normalizedRaw: node.normalizedRaw,
    };
  }

  if (node.type === 'call') {
    return {
      type: 'call',
      target: node.expression,
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'goto') {
    return {
      type: 'goto',
      target: node.expression,
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'exit') {
    return {
      type: 'exit',
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'window') {
    return {
      type: 'window',
      target: node.id,
      title: node.title,
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'default') {
    return {
      type: 'default',
      defaultType: node.defaultType || 'unknown',
      target: node.target,
      suffix: node.suffix,
      targetSuffix: node.targetSuffix,
      value: node.value,
      valueSuffix: node.valueSuffix,
      valueType: node.valueType,
      isSelfAssignment: node.isSelfAssignment,
      catalogId: node.catalogId,
      options: node.options,
      parseStatus: node.parseStatus || 'unknown',
      visible: false,
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'read') {
    return {
      type: 'read',
      source: node.source,
      row: node.row,
      column: node.column,
      search: node.search,
      target: node.target,
      targets: node.targets || [],
      until: node.until,
      visible: false,
      line: node.line,
      raw: node.raw,
    };
  }

  if (node.type === 'if') {
    return {
      type: 'if',
      expression: node.expression,
      line: node.line,
      raw: node.raw,
    };
  }

  return null;
}

function createMenuItem(text, node, textLine = node.line) {
  const action = createActionFromNode(node);
  const item = {
    text,
    value: safeMenuValue(text),
    action,
    actions: action ? [action] : [],
    actionLines: [node.line],
    actionType: action ? action.type : 'unknown',
    line: textLine,
    raw: node.raw,
  };

  return item;
}

function createSubmenuItem(text, submenuNode, textLine = submenuNode.line) {
  const action = {
    type: 'submenu',
    target: `${submenuNode.id}:${submenuNode.line}`,
    targetId: submenuNode.id,
    title: submenuNode.title,
    line: submenuNode.line,
  };

  return {
    text,
    value: safeMenuValue(text),
    actionType: 'submenu',
    target: submenuNode.id,
    action,
    actions: [action],
    actionLines: [submenuNode.line],
    line: textLine,
    raw: submenuNode.raw,
  };
}

function appendMenuAction(menu, node) {
  const action = createActionFromNode(node);

  if (!action) {
    return false;
  }

  if (menu.pendingText) {
    const item = createMenuItem(menu.pendingText, node, menu.pendingLine || node.line);
    menu.items.push(item);
    menu.currentItem = item;
    menu.pendingText = null;
    menu.pendingLine = null;
    return true;
  }

  if (menu.currentItem) {
    menu.currentItem.actions.push(action);
    menu.currentItem.actionLines.push(node.line);
    return true;
  }

  return false;
}

function applyButtonDefinitions(ir) {
  ir.forms = ir.forms.map((form) => ({
    ...form,
    fields: (form.fields || []).map((field) => {
      if (field.type !== 'button') {
        return field;
      }

      const button = field.button || field.action;
      const buttonDefinition = ir.buttonDefinitions[button];

      if (!buttonDefinition) {
        return field;
      }

      return {
        ...field,
        label: buttonDefinition.label,
        button,
        buttonDefinition: {
          label: buttonDefinition.label,
          line: buttonDefinition.line,
        },
      };
    }),
  }));
}

function extractVisualControls(raw, variableCatalog, catalogs, line, context = {}) {
  const controls = [];
  const matchedReferences = new Set();
  const referenceMatches = collectBracketMatches(raw);
  let previousReferenceEnd = 0;
  let match;

  BRACKET_REFERENCE_PATTERN.lastIndex = 0;

  while ((match = BRACKET_REFERENCE_PATTERN.exec(raw)) !== null) {
    const reference = match[1];
    const labelSegment = raw.slice(previousReferenceEnd, match.index);

    if (matchedReferences.has(reference.toUpperCase())) {
      previousReferenceEnd = BRACKET_REFERENCE_PATTERN.lastIndex;
      continue;
    }

    controls.push(createVisualControl(getReferenceLabel(labelSegment, reference), reference, variableCatalog, catalogs, line, raw, context.buttonDefinitions));
    matchedReferences.add(reference.toUpperCase());
    previousReferenceEnd = BRACKET_REFERENCE_PATTERN.lastIndex;
  }

  const checkboxControls = controls.filter((control) => control.type === 'field' && hasCheckboxSuffix(control.variable));

  if (checkboxControls.length > 1 && checkboxControls.length === controls.length) {
    return [createCheckboxGroup(checkboxControls, context.previousTextLabel, line, raw)];
  }

  const compositeMatches = referenceMatches.filter((referenceMatch) => isCompositeFieldReference(referenceMatch.reference));

  if (compositeMatches.length > 1) {
    return [createCompositeFieldRow(raw, compositeMatches, variableCatalog, catalogs, line, context.buttonDefinitions)];
  }

  if (controls.length === 0) {
    const textControl = extractTextControl(raw, line);

    if (textControl) {
      controls.push(textControl);
    }
  }

  return controls;
}

function collectControlDiagnostics(fields) {
  return fields.flatMap((field) => [
    ...(field.diagnostic ? [field.diagnostic] : []),
    ...((field.parts || []).flatMap((part) => (part.diagnostic ? [part.diagnostic] : []))),
  ]);
}

function stripCatalogSuffix(variableName) {
  return variableName.replace(/#[A-Z]$/i, '');
}

function normalizeVariableReference(variableName) {
  return stripCatalogSuffix(variableName).toUpperCase();
}

function createCatalogFromDefault(node) {
  return {
    id: node.catalogId || `${node.target}Catalog`,
    type: 'select',
    sourceVariable: node.targetSuffix ? `${node.target}${node.targetSuffix}` : `${node.target}${node.suffix || ''}`,
    variable: node.target,
    options: node.options || [],
    line: node.line,
    endLine: node.endLine || node.line,
  };
}

function commandToAction(commandText) {
  if (!commandText) {
    return null;
  }

  const commandMatch = commandText.trim().match(COMMAND_PATTERN);

  if (!commandMatch) {
    return {
      type: 'raw',
      value: commandText.trim(),
    };
  }

  const command = commandMatch[1].toLowerCase();
  const expression = commandMatch[2].trim();

  if (command === 'call') {
    return {
      type: 'call',
      target: expression,
    };
  }

  if (command === 'goto') {
    return {
      type: 'goto',
      target: expression,
    };
  }

  return {
    type: command,
    expression,
  };
}

function parseIfExpression(expression, line) {
  const match = expression.match(IF_PATTERN);

  if (!match) {
    return {
      type: 'condition',
      line,
      raw: expression,
      parsed: false,
    };
  }

  return {
    type: 'condition',
    line,
    parsed: true,
    when: {
      variable: match[1],
      operator: match[2],
      value: normalizeValue(match[3]),
    },
    action: commandToAction(match[4]),
  };
}

function createRemarkTemplate(raw, line) {
  PLACEHOLDER_PATTERN.lastIndex = 0;

  if (!PLACEHOLDER_PATTERN.test(raw)) {
    PLACEHOLDER_PATTERN.lastIndex = 0;
    return null;
  }

  PLACEHOLDER_PATTERN.lastIndex = 0;

  return {
    type: 'remark',
    template: raw.replace(PLACEHOLDER_PATTERN, '{$1}'),
    line,
  };
}

function pushLogic(ir, node) {
  if (node.diagnostic || (node.diagnostics || []).length > 0) {
    ir.diagnostics.push(
      ...(node.diagnostics || [node.diagnostic]).filter(Boolean).map((diagnostic) => ({
        type: 'diagnostic',
        line: node.line,
        raw: node.raw,
        ...diagnostic,
      }))
    );
  }

  if (node.type === 'if') {
    ir.logic.push(parseIfExpression(node.expression, node.line));
    return;
  }

  if (node.type === 'read') {
    ir.logic.push({
      type: 'read',
      source: node.source,
      row: node.row,
      column: node.column,
      search: node.search,
      target: node.target,
      targets: node.targets || [],
      variables: node.variables || [],
      literals: node.literals || [],
      until: node.until,
      parseStatus: node.parseStatus,
      visible: false,
      diagnostic: node.diagnostic,
      diagnostics: node.diagnostics || [],
      line: node.line,
      raw: node.raw,
    });
    return;
  }

  if (node.type === 'call') {
    ir.calls.push({
      type: 'call',
      target: node.expression,
      line: node.line,
    });
    return;
  }

  ir.logic.push({
    type: node.type,
    expression: node.expression,
    line: node.line,
  });
}

function getNodeRaw(node) {
  return node.raw || node.expression || node.text || '';
}

function hasErrorAction(text) {
  return /\b(?:THEN\s+)?WINDOW\s+ERROR\b/i.test(text) || /\bGOTO\s+[A-Z0-9_]*ERROR[A-Z0-9_]*\b/i.test(text);
}

function findLogicRequiredVariables(node, nextNode) {
  if (node.type !== 'if') {
    return [];
  }

  const expression = node.expression || '';
  const actionText = `${expression} ${nextNode ? getNodeRaw(nextNode) : ''}`;

  if (!hasErrorAction(actionText)) {
    return [];
  }

  const requiredVariables = [];
  let match;

  REQUIRED_LOGIC_VALUE_PATTERN.lastIndex = 0;

  while ((match = REQUIRED_LOGIC_VALUE_PATTERN.exec(expression)) !== null) {
    requiredVariables.push(stripCatalogSuffix(match[1]));
  }

  return requiredVariables;
}

function applyRequiredValidation(ir, variableName) {
  const normalizedVariable = normalizeVariableReference(variableName);

  Object.values(ir.variables).forEach((variable) => {
    if (normalizeVariableReference(variable.name) !== normalizedVariable) {
      return;
    }

    variable.required = true;
    variable.validationSource = 'logic';
  });

  ir.forms.forEach((form) => {
    form.fields = form.fields.map((field) => {
      if (field.type === 'field_row') {
        return {
          ...field,
          parts: (field.parts || []).map((part) => {
            if (!part.variable || normalizeVariableReference(part.variable) !== normalizedVariable) {
              return part;
            }

            return {
              ...part,
              required: true,
              validationSource: 'logic',
            };
          }),
        };
      }

      if (!field.variable || normalizeVariableReference(field.variable) !== normalizedVariable) {
        return field;
      }

      return {
        ...field,
        required: true,
        validationSource: 'logic',
      };
    });
  });
}

function applyLogicRequiredValidations(ast, ir) {
  ast.nodes.forEach((node, index) => {
    findLogicRequiredVariables(node, ast.nodes[index + 1]).forEach((variableName) => {
      applyRequiredValidation(ir, variableName);
    });
  });
}

function isEditableControl(field) {
  if (field.visible === false || field.capturable === false || field.readonly || field.disabled) {
    return false;
  }

  if (field.type === 'field_row') {
    return (field.parts || []).some((part) => {
      return (
        part.visible !== false &&
        part.capturable !== false &&
        !part.readonly &&
        !part.disabled &&
        (part.type === 'field' ||
          part.type === 'select' ||
          part.controlType === 'input' ||
          part.control === 'input' ||
          part.controlType === 'checkbox' ||
          part.control === 'checkbox')
      );
    });
  }

  return (
    field.type === 'field' ||
    field.type === 'select' ||
    field.controlType === 'input' ||
    field.control === 'input' ||
    field.type === 'checkbox-group' ||
    field.controlType === 'checkbox' ||
    field.control === 'checkbox' ||
    field.controlType === 'date' ||
    field.controlType === 'hour' ||
    field.controlType === 'mail' ||
    field.controlType === 'button_menu'
  );
}

function getWindowMetrics(form) {
  const controls = form.fields || [];

  return {
    fieldCount: controls.filter(isEditableControl).length,
    messageCount: controls.filter((field) => field.type === 'text' || field.type === 'system').length,
    buttonCount: controls.filter((field) => field.type === 'button').length,
  };
}

function classifyWindow(form, metrics = getWindowMetrics(form)) {
  if (metrics.fieldCount > 0) {
    return 'form';
  }

  if (metrics.buttonCount > 0) {
    return 'confirmation';
  }

  return 'validation';
}

function classifyWindows(ir) {
  ir.forms = ir.forms.map((form) => {
    const metrics = getWindowMetrics(form);

    return {
      ...form,
      ...metrics,
      windowType: classifyWindow(form, metrics),
    };
  });
}

function createUnknownLineDiagnostics(lineClassifications = []) {
  return lineClassifications
    .filter((lineClassification) => lineClassification.type === 'unknown' && lineClassification.diagnostic)
    .map((lineClassification) => ({
      type: 'diagnostic',
      level: 'warning',
      code: 'UNKNOWN_SST_LINE',
      message: 'Linea SST no clasificada con certeza. Se oculta del Preview para evitar inventar UI.',
      line: lineClassification.line,
      raw: lineClassification.raw,
    }));
}

function buildMenuTree(menus = []) {
  const menuByKey = new Map(menus.map((menu) => [menu.key, menu]));
  const menuById = new Map(menus.map((menu) => [menu.id, menu]));

  function buildMenu(menu, ancestors = new Set()) {
    if (!menu || ancestors.has(menu.key)) {
      return null;
    }

    const nextAncestors = new Set(ancestors);
    nextAncestors.add(menu.key);

    return {
      ...menu,
      items: (menu.items || []).map((item) => {
        const submenuAction = (item.actions || []).find((action) => action.type === 'submenu');
        const submenuTarget = item.actionType === 'submenu' ? item.target : submenuAction?.target;
        const submenu = submenuTarget ? buildMenu(menuByKey.get(submenuTarget) || menuById.get(submenuTarget), nextAncestors) : null;
        const actions = (item.actions || []).map((action) => {
          if (action.type !== 'submenu' || !submenu) {
            return action;
          }

          return {
            ...action,
            menu: submenu,
          };
        });

        return submenu
          ? {
              ...item,
              action: item.action?.type === 'submenu' ? { ...item.action, menu: submenu } : item.action,
              actions,
              submenu,
            }
          : {
              ...item,
              actions,
            };
      }),
    };
  }

  return menus.filter((menu) => !menu.parentKey).map((menu) => buildMenu(menu)).filter(Boolean);
}

function getMenuActionType(item = {}) {
  if (['submenu', 'actions', 'call', 'goto', 'exit', 'showWindow'].includes(item.actionType)) {
    return item.actionType;
  }

  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu');
  const executableActions = actions.filter((action) => action.type !== 'submenu');

  if (submenuAction) {
    return 'submenu';
  }

  if (executableActions.length === 0) {
    return 'unknown';
  }

  if (executableActions.length === 1 && ['call', 'goto', 'exit', 'window'].includes(executableActions[0].type)) {
    return executableActions[0].type === 'window' ? 'showWindow' : executableActions[0].type;
  }

  return 'actions';
}

function getMenuItemTarget(item = {}) {
  if (item.target) {
    return item.target;
  }

  const actions = item.actions || [];
  const submenuAction = actions.find((action) => action.type === 'submenu');

  if (submenuAction) {
    return submenuAction.targetId || submenuAction.target;
  }

  const action = actions.find((candidate) => ['call', 'goto', 'window'].includes(candidate.type));

  if (!action) {
    return null;
  }

  return action.target || action.formRef || action.id || null;
}

function normalizeMenuItemForIr(item = {}) {
  const actionType = getMenuActionType(item);

  return {
    ...item,
    value: item.value || safeMenuValue(item.text),
    actionType,
    target: actionType === 'actions' ? null : item.target || getMenuItemTarget(item),
  };
}

function getNodeIndent(node = {}) {
  if (typeof node.indent === 'number') {
    return node.indent;
  }

  const raw = node.raw || node.normalizedRaw || '';
  const match = String(raw).match(/^\s*/);
  return match ? match[0].length : 0;
}

function isMenuLineNode(node = {}) {
  return node.type === 'menu' || /^MENU\b/i.test(String(node.raw || node.normalizedRaw || '').trim());
}

function getQuotedMenuOptionText(node = {}) {
  const raw = String(node.raw || node.normalizedRaw || '').trim();
  const match = raw.match(/^"([^"]+)"/);

  return match ? cleanVisibleLabel(match[1]) : null;
}

function isExecutableMenuActionNode(node = {}) {
  return ['default', 'call', 'goto', 'if', 'command', 'remark', 'ssr', 'window', 'read', 'compute', 'require', 'exit'].includes(node.type);
}

function createIndentMenuNode(node = {}, index = 0, parentMenu = null) {
  const indent = getNodeIndent(node);
  const titleMatch = String(node.raw || node.normalizedRaw || '').match(/\bH\s*=\s*"([^"]*)"/i);
  const rowMatch = String(node.raw || node.normalizedRaw || '').match(/\bR\s*=\s*(\d+)/i);
  const columnMatch = String(node.raw || node.normalizedRaw || '').match(/\bC\s*=\s*(\d+)/i);
  const id = node.id && /^MENU_/i.test(node.id) ? node.id : `MENU_${index}`;

  return {
    id,
    key: id,
    type: 'menu',
    title: node.title || (titleMatch ? titleMatch[1] : 'MENU'),
    row: node.row ?? (rowMatch ? Number(rowMatch[1]) : null),
    column: node.column ?? (columnMatch ? Number(columnMatch[1]) : null),
    indent,
    line: node.line,
    parent: parentMenu?.id || null,
    parentId: parentMenu?.id || null,
    parentKey: parentMenu?.id || null,
    parentTitle: parentMenu?.title || null,
    options: [],
    items: [],
  };
}

function createIndentMenuOption(node = {}, text) {
  return {
    text,
    value: safeMenuValue(text),
    line: node.line,
    indent: getNodeIndent(node),
    actionType: null,
    target: null,
    actions: [],
    actionLines: [],
    raw: node.raw,
  };
}

function normalizeMenuAction(action) {
  if (!action) {
    return null;
  }

  if (action.type === 'remark') {
    return {
      ...action,
      template: (action.template || '').replace(/\{@DATE\}/gi, '{DATE}').replace(/\{@TIME\}/gi, '{TIME}'),
    };
  }

  return action;
}

function buildMenusByIndent(ast) {
  const menus = [];
  const menuDebug = [];
  const menuStack = [];
  const currentOptionByMenuId = {};
  let menuIndex = 0;

  (ast.nodes || []).forEach((node) => {
    const indent = getNodeIndent(node);

    if ((node.type === 'label' || node.type === 'subroutine') && menuStack.length > 0) {
      while (menuStack.length && indent <= menuStack[menuStack.length - 1].indent) {
        menuStack.pop();
      }
    }

    if (isMenuLineNode(node)) {
      while (menuStack.length && indent <= menuStack[menuStack.length - 1].indent) {
        menuStack.pop();
      }

      const parentMenu = menuStack[menuStack.length - 1] || null;
      menuIndex += 1;
      const menu = createIndentMenuNode(node, menuIndex, parentMenu);

      if (parentMenu) {
        const parentCurrentOption = currentOptionByMenuId[parentMenu.id];

        if (parentCurrentOption) {
          parentCurrentOption.actionType = 'submenu';
          parentCurrentOption.target = menu.id;
          parentCurrentOption.actions = [];
          parentCurrentOption.actionLines = [];
        }
      }

      menus.push(menu);
      menuStack.push(menu);
      menuDebug.push({
        line: node.line,
        indent,
        classification: 'menu_start',
        menuId: menu.id,
        title: menu.title,
        parent: menu.parent,
      });
      return;
    }

    if (menuStack.length > 1) {
      while (menuStack.length > 1 && indent <= menuStack[menuStack.length - 1].indent) {
        menuStack.pop();
      }
    }

    const optionText = getQuotedMenuOptionText(node);

    if (optionText && menuStack.length > 0 && !isMenuSeparator(optionText)) {
      const currentMenu = menuStack[menuStack.length - 1];
      const option = createIndentMenuOption(node, optionText);

      currentMenu.options.push(option);
      currentMenu.items.push(option);
      currentOptionByMenuId[currentMenu.id] = option;
      menuDebug.push({
        line: node.line,
        indent,
        classification: 'menu_option',
        menuId: currentMenu.id,
        text: optionText,
      });
      return;
    }

    if (menuStack.length === 0 || !isExecutableMenuActionNode(node)) {
      return;
    }

    const currentMenu = menuStack[menuStack.length - 1];
    const currentOption = currentOptionByMenuId[currentMenu.id];

    if (!currentOption) {
      return;
    }

    const action = normalizeMenuAction(createActionFromNode(node));

    if (!action) {
      return;
    }

    currentOption.actions.push(action);
    currentOption.actionLines.push(node.line);

    if (!currentOption.actionType) {
      currentOption.actionType = 'actions';
    }

    menuDebug.push({
      line: node.line,
      indent,
      classification: 'menu_action',
      menuId: currentMenu.id,
      option: currentOption.text,
      actionType: action.type,
    });
  });

  const normalizedMenus = menus.map((menu) => {
    const options = (menu.options || []).map(normalizeMenuItemForIr);

    return {
      ...menu,
      options,
      items: options,
    };
  });

  return {
    menus: normalizedMenus,
    menuDebug,
  };
}

export function analyzeSemantics(ast, title = 'sin-nombre.sst') {
  const ir = createEmptyIr(title);
  ir.lineClassifications = ast.lineClassifications || [];
  ir.specialCharReport = ast.specialCharReport || ir.specialCharReport;
  ir.diagnostics.push(...(ast.specialCharDiagnostics || []));
  ir.diagnostics.push(...createUnknownLineDiagnostics(ir.lineClassifications));
  let activeForm = null;
  let activeMenu = null;
  let suspendedMenu = null;
  const menuStack = [];

  ast.nodes.forEach((node) => {
    if (node.type !== 'variable') {
      return;
    }

    ir.variables[node.name] = {
      name: node.name,
      sourceVariable: node.sourceVariable,
      suffix: node.suffix,
      datatype: node.datatype,
      length: node.length,
      required: Boolean(node.required),
      validationSource: node.required ? 'define' : null,
      label: node.label,
      line: node.line,
    };
  });

  ast.nodes.forEach((node) => {
    if (node.type === 'variable') {
      return;
    }

    if (node.type === 'format' || node.type === 'sabre_special_sequence') {
      return;
    }

    if (node.type === 'label') {
      ir.labels.push({
        name: node.name,
        line: node.line,
        raw: node.raw,
      });
      return;
    }

    if (node.type === 'button_definition') {
      ir.buttonDefinitions[node.button] = {
        button: node.button,
        label: node.label,
        line: node.line,
        raw: node.raw,
      };
      return;
    }

    if (node.type === 'window') {
      if (activeMenu) {
        appendMenuAction(activeMenu, node);
        suspendedMenu = activeMenu;
      }

      activeMenu = null;
      activeForm = {
        id: node.id,
        title: node.title,
        fields: [],
        line: node.line,
        lastTextLabel: null,
        lastTextLine: null,
      };
      ir.forms.push(activeForm);
      return;
    }

    if (node.type === 'menu') {
      const parentMenu = activeMenu;

      activeForm = null;

      if (parentMenu?.pendingText) {
        const submenuItem = createSubmenuItem(parentMenu.pendingText, node, parentMenu.pendingLine || node.line);
        parentMenu.items.push(submenuItem);
        parentMenu.currentItem = submenuItem;
        parentMenu.pendingText = null;
        parentMenu.pendingLine = null;
      }

      if (parentMenu) {
        menuStack.push(parentMenu);
      }

      activeMenu = {
        type: 'menu',
        id: node.id,
        key: `${node.id}:${node.line}`,
        title: node.title,
        row: node.row,
        column: node.column,
        parentId: parentMenu?.id || null,
        parentKey: parentMenu?.key || null,
        parentTitle: parentMenu?.title || null,
        items: [],
        pendingText: null,
        pendingLine: null,
        currentItem: null,
        line: node.line,
      };
      ir.menus.push(activeMenu);
      return;
    }

    if (node.type === 'endmenu') {
      activeMenu = menuStack.pop() || null;

      if (activeMenu) {
        activeMenu.currentItem = null;
      }

      return;
    }

    if (node.type === 'endwindow') {
      activeForm = null;

      if (suspendedMenu) {
        activeMenu = suspendedMenu;
        suspendedMenu = null;
      }

      return;
    }

    if (node.type === 'unknown') {
      if (activeMenu) {
        const text = node.lineClassification?.text || extractMenuText(node.raw);

        if (text && node.lineClassification?.type !== 'menu_separator' && !isMenuSeparator(text)) {
          activeMenu.currentItem = null;
          activeMenu.pendingText = text;
          activeMenu.pendingLine = node.line;
        } else {
          activeMenu.currentItem = null;
          activeMenu.pendingText = null;
          activeMenu.pendingLine = null;
        }

        return;
      }

      if (activeForm && node.lineClassification?.visible === false) {
        ir.unresolved.push({
          type: 'unknown',
          visible: false,
          diagnostic: true,
          raw: node.raw,
          line: node.line,
        });
        return;
      }

      const fields = activeForm
        ? extractVisualControls(node.raw, ir.variables, ir.catalogs, node.line, {
            previousTextLabel: activeForm.lastTextLabel,
            buttonDefinitions: ir.buttonDefinitions,
          })
        : [];

      if (fields.length > 0) {
        if (fields.length === 1 && fields[0].type === 'checkbox-group' && activeForm.lastTextLine) {
          activeForm.fields = activeForm.fields.filter((field) => field.line !== activeForm.lastTextLine || field.type !== 'text');
        }

        activeForm.fields.push(...fields);
        ir.diagnostics.push(...collectControlDiagnostics(fields));

        if (fields.length === 1 && fields[0].type === 'text') {
          activeForm.lastTextLabel = fields[0].label;
          activeForm.lastTextLine = fields[0].line;
        } else {
          activeForm.lastTextLabel = null;
          activeForm.lastTextLine = null;
        }

        return;
      }

      const remark = createRemarkTemplate(node.raw, node.line);

      if (remark) {
        ir.remarks.push(remark);
        return;
      }

      ir.unresolved.push({
        type: 'unknown',
        raw: node.raw,
        line: node.line,
      });
      return;
    }

    if (node.type === 'remark') {
      if (activeMenu) {
        appendMenuAction(activeMenu, node);
      }

      ir.remarks.push({
        type: 'remark',
        remarkType: node.remarkType || 'Historical',
        template: (node.template || node.text || '').replace(PLACEHOLDER_PATTERN, '{$1}'),
        skip: node.skip !== false,
        line: node.line,
        raw: node.raw,
      });
      return;
    }

    if (node.type === 'default') {
      const defaultAction = {
        type: 'default',
        defaultType: node.defaultType || 'unknown',
        target: node.target,
        suffix: node.suffix,
        targetSuffix: node.targetSuffix,
        value: node.value,
        valueSuffix: node.valueSuffix,
        valueType: node.valueType,
        isSelfAssignment: node.isSelfAssignment,
        catalogId: node.catalogId,
        options: node.options,
        visible: false,
        parseStatus: node.parseStatus || 'unknown',
        diagnostic: node.diagnostic,
        line: node.line,
        endLine: node.endLine || node.line,
        raw: node.raw,
      };

      ir.defaults.push(defaultAction);

      if (activeMenu) {
        appendMenuAction(activeMenu, node);
      }

      if (node.diagnostic || (node.diagnostics || []).length > 0) {
        ir.diagnostics.push(
          ...(node.diagnostics || [node.diagnostic]).filter(Boolean).map((diagnostic) => ({
            type: 'diagnostic',
            line: node.line,
            raw: node.raw,
            ...diagnostic,
          }))
        );
      }

      if (node.defaultType === 'default_catalog') {
        const catalog = createCatalogFromDefault(node);
        ir.catalogs[catalog.variable] = catalog;
        ir.forms.forEach((form) => {
          form.fields = form.fields.map((field) => enrichField(field, ir.variables, ir.catalogs));
        });
      }

      return;
    }

    if (activeMenu && ['call', 'goto', 'exit', 'command', 'remark', 'ssr', 'read', 'if'].includes(node.type)) {
      appendMenuAction(activeMenu, node);
      return;
    }

    if (activeMenu && !['call', 'goto', 'exit', 'command', 'remark', 'ssr', 'read', 'if'].includes(node.type)) {
      activeMenu = null;
    }

    if (['command', 'ssr', 'subroutine', 'sabre_special_sequence'].includes(node.type)) {
      return;
    }

    if (['if', 'else', 'endif', 'call', 'goto', 'write', 'read', 'compute', 'require', 'exit'].includes(node.type)) {
      pushLogic(ir, node);
    }
  });

  applyButtonDefinitions(ir);
  applyLogicRequiredValidations(ast, ir);
  classifyWindows(ir);

  const indentMenuAnalysis = buildMenusByIndent(ast);

  if (indentMenuAnalysis.menus.length > 0) {
    ir.menus = indentMenuAnalysis.menus;
    ir.menuDebug = indentMenuAnalysis.menuDebug;
  }

  ir.menus = ir.menus.map(({ pendingText, pendingLine, currentItem, ...menu }) => {
    const items = (menu.items || []).map(normalizeMenuItemForIr);

    return {
      ...menu,
      parent: menu.parentId || null,
      items,
      options: items,
    };
  });
  ir.menuTree = buildMenuTree(ir.menus);

  return ir;
}
